/**
 * StudyLocationDialog — Read-only info dialog showing study storage
 * location across nodes/stores (admin only).
 */
import { useEffect, useState, startTransition } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study, StudyLocationResult } from '../../api/types';

interface StudyLocationDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

export const StudyLocationDialog = ({
  open,
  study,
  onClose,
}: StudyLocationDialogProps) => {
  const [data, setData] = useState<StudyLocationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !study) return;
    startTransition(() => { setLoading(true); setError(null); });
    api
      .getStudyLocation(study.objKey)
      .then((result) => {
        setData(result);
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [open, study]);

  if (!study) return null;

  const renderMap = (label: string, map: Record<string, string> | undefined) => {
    if (!map || Object.keys(map).length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>UUID</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(map).map(([uuid, status]) => (
              <TableRow key={uuid}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {uuid}
                </TableCell>
                <TableCell>{status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Study Locations — {study.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {data && !loading && (
          <>
            {data.studyUID && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Study UID: <code>{data.studyUID}</code>
              </Typography>
            )}
            {data.totalObjects != null && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Total Objects: {data.totalObjects}
              </Typography>
            )}
            {renderMap('Nodes', data.nodes)}
            {renderMap('Stores', data.stores)}
            {!Object.keys(data.nodes).length && !Object.keys(data.stores).length && (
              <Typography variant="body2" color="text.secondary">
                No location data available.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
